/**
 * 66.8, digging in — a dig's shape (decision 160; ARCHITECTURE §5.18). Lane E's geometry, moved here from its look
 * (rendering/digLook.ts, which keeps the colours and reads this) so the sim and the look share one shape: the fine ground
 * reads a dig's heights (core/fineTerrain.ts), the cover map its two numbers. Two digs, each held by one person facing +z
 * (the enemy): a **scrape** (a body-long hollow for a prone holder, its spoil a berm in front) and a **foxhole** (a pit dug to
 * a kneeling depth, a parapet in front and at its sides). A hole is dug until the holder's head is at the lip, whatever the
 * ground: the material sets its walls and lip (and how long it takes, the entrench rule), not how much it covers. Rock and
 * ice refuse a dig (null). Person units (a person is 1.8). Pure.
 */
import type { GroundMaterial } from '../types';
import { COVER_POSE_SIZE } from '../constants/action';
import { dpow, dsin } from './dmath';

/** A dig's walls and lip by the ground (lane E's): how soft the walls are (person units they lean back over the depth), and
 *  × the dig's lip height and × its width (the depth makes up the rest of the cover). Null: the ground refuses a dig. */
export interface DigGround { wall: number; lip: number; lipWide: number }
export const DIG_GROUND: Record<GroundMaterial, DigGround | null> = {
  soil: { wall: 0.12, lip: 1, lipWide: 1 },
  sand: { wall: 0.5, lip: 0.55, lipWide: 1.7 },
  snow: { wall: 0.04, lip: 0.9, lipWide: 0.8 },
  mud: { wall: 0.22, lip: 0.75, lipWide: 1.25 },
  ash: { wall: 0.38, lip: 0.6, lipWide: 1.5 },
  salt: { wall: 0.1, lip: 0.5, lipWide: 1 },
  rock: null,
  ice: null,
  scree: null,   // (74.13 step 4: loose rock, not dug — as rock)
};

/**
 * The two digs at full (person units; the hole's centre at the origin, the holder at `hold` along z):
 * - `cover`: the lip's top over the floor, the same on every ground (the scrape's: a prone holder's eyes clear it propped
 *   up; the foxhole's: a kneeling holder's head goes under it leaning in, and the holder kneels up over it to fire, as over
 *   the barrels — COVER_MODEL_SIZE.barrels, 0.95);
 * - `lip`: the lip's height over the ground on soil (× the material's `lip`; the depth is the rest);
 * - the hole's half size and centre, and where the spoil heaps in front (`lipFrom` … `lipTo` on soil, × `lipWide`).
 */
export const DIGS = {
  scrape: { pose: 'crest', cover: 0.6, lip: 0.25, halfX: 0.42, halfZ: 1.12, centreZ: -0.3, hold: 0, lipFrom: 0.85, lipTo: 1.4 },
  foxhole: { pose: 'low', cover: 0.95, lip: 0.28, halfX: 0.52, halfZ: 0.55, centreZ: 0, hold: 0.1, lipFrom: 0.62, lipTo: 1.2 },
} as const;
export type DigKind = keyof typeof DIGS;

/** How far (person units, from the dig's centre) its shape can reach: the hole, the walls' lean and the spoil in front. */
export const DIG_REACH = 2.6;
/** The step (person heights) a line is sampled at where it passes a dig: a berm is narrower than the sight march's step. */
export const DIG_STEP_PEOPLE = 0.1;

const smooth = (e0: number, e1: number, x: number) => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
/** 1 well inside a rounded box (centre (cx, cz), half-size (hx, hz)), 0 outside, over a soft edge `soft` wide inside it. */
function inBox(x: number, z: number, cx: number, cz: number, hx: number, hz: number, soft: number): number {
  const dx = Math.abs(x - cx) - hx, dz = Math.abs(z - cz) - hz;
  const ox = Math.max(dx, 0), oz = Math.max(dz, 0);
  const d = Math.sqrt(ox * ox + oz * oz) + Math.min(Math.max(dx, dz), 0);
  return smooth(0, Math.max(0.02, soft), -d);
}
/** A band of spoil from z0 to z1 across |x − cx| < hx, `h` high, rounded across and eased at its ends. */
function bank(x: number, z: number, z0: number, z1: number, hx: number, h: number, cx = 0): number {
  const t = (z - z0) / (z1 - z0);
  if (t <= 0 || t >= 1) return 0;
  return h * dpow(dsin(Math.PI * t), 0.8) * (1 - smooth(hx - 0.25, hx + 0.1, Math.abs(x - cx)));   // (dmath: the same bits in every engine)
}

export interface DigGeometry {
  ground: DigGround;
  /** the floor's depth under the ground and the lip's top over it (`depth + top` is the cover) */
  depth: number; top: number;
  /** the floor's share of the depth at (x, z), 0 … 1 */
  pit: (x: number, z: number) => number;
  /** the spoil's height over the ground at (x, z) */
  spoil: (x: number, z: number) => number;
  /** the ground's height at (x, z): −depth · pit + spoil */
  height: (x: number, z: number) => number;
}

/** A dig of `kind` in `material`, `done` of the way dug (0 … 1: the hole deepens and its spoil grows as it is dug). Null where the ground refuses a dig (rock, ice). */
export function digGeometry(kind: DigKind, material: GroundMaterial, done = 1): DigGeometry | null {
  const ground = DIG_GROUND[material];
  if (!ground) return null;
  const D = DIGS[kind], k = Math.max(0, Math.min(1, done));
  const lip = D.lip * ground.lip, depth = (D.cover - lip) * k, top = lip * k, wide = (D.lipTo - D.lipFrom) * ground.lipWide;
  // the walls lean back by `wall` over the depth: the box grows by half of it and its edge softens over the rest
  const soft = (kind === 'scrape' ? 0.18 : 0.06) + ground.wall, grow = ground.wall * 0.5;
  const pit = (x: number, z: number) => inBox(x, z, 0, D.centreZ, D.halfX + grow, D.halfZ + grow, soft);
  const from = D.lipFrom + grow;
  const spoil = kind === 'scrape'
    ? (x: number, z: number) => bank(x, z, from, from + wide, 0.75, top) + bank(z, x, 0.5 + grow, 0.5 + grow + 0.4 * ground.lipWide, 1.0, top * 0.3, D.centreZ) + bank(z, x, -0.5 - grow - 0.4 * ground.lipWide, -0.5 - grow, 1.0, top * 0.3, D.centreZ)
    : (x: number, z: number) => bank(x, z, from, from + wide, 1.0, top) + bank(z, x, from, from + wide * 0.85, 0.8, top * 0.75, 0.15) + bank(z, x, -from - wide * 0.85, -from, 0.8, top * 0.75, 0.15);
  return { ground, depth, top, pit, spoil, height: (x, z) => -depth * pit(x, z) + spoil(x, z) };
}

/**
 * The cover a dug holder's pressed pose takes (PersonAnim.cover — the cover map's held point carries the same two numbers):
 * its `height` over the floor (the lip's top) and the dug face's `gap` from the holder at its chest (the face found by
 * stepping from the holder toward the enemy until the ground stands at that height; as cover-pose-2's rock profile measures
 * a boulder's). Null where nothing is dug.
 */
export function digCover(kind: DigKind, material: GroundMaterial, done = 1): { pose: 'crest' | 'low'; height: number; gap: number; floor: number } | null {
  const s = digGeometry(kind, material, done);
  if (!s) return null;
  const D = DIGS[kind], chest = COVER_POSE_SIZE[D.pose].down.eye - 0.3, at = -s.depth + chest;
  let z = D.hold;
  while (z < D.hold + 3 && s.height(0, z) < at) z += 0.005;
  return { pose: D.pose, height: s.depth + s.top, gap: z - D.hold, floor: -s.depth };
}
